import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import type { ActivityFeedItem } from '@/lib/api/portfolio';
import { auditOperationLabel, describeAuditEvent } from '@/lib/audit-text';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/format';

/**
 * Portfolio activity feed (T-0107 / C20-117).
 *
 * Right-rail sidebar on the portfolio landing route. Surfaces the most
 * recent mutations across every engagement the caller can see — the
 * "what changed lately" view that anchors a partner's morning sweep.
 *
 * Source: `useActivityFeedQuery()` in `lib/api/portfolio.ts`. That hook
 * fans out one audit call per engagement and merges client-side. The
 * unified cross-engagement audit endpoint doesn't exist today; see the
 * ticket's §6 risks for the backend follow-up.
 *
 * Visual contract: `prototype/screens-portfolio.js → activityItem()`:
 *   - 380px-ish width sidebar with a posture-coloured dot prefix
 *   - "{engagement_name} · {summary}" message line
 *   - {relative timestamp} meta line ("2h ago")
 *   - "View all" link in the header navigates to /notifications
 *
 * The summary is the audit trail's own sentence (`describeAuditEvent`,
 * C2-30): "Approved contract fact: Vendor", never an operation string or an
 * id. The stored operation is the row's tooltip.
 *
 * Each row links to the parent engagement's overview page — clicking
 * lands the user on the engagement that fired the mutation, where the
 * audit trail tab provides the full context.
 *
 * Empty state: tighten copy when the feed is empty so the dashboard
 * doesn't render a vacant rail.
 */
export interface ActivityFeedProps {
  items: ActivityFeedItem[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  className?: string;
}

export function ActivityFeed({ items, isLoading, isError, error, className }: ActivityFeedProps) {
  return (
    <aside
      className={cn(
        'flex w-full flex-col rounded-card border border-ink-100 bg-paper-0',
        className,
      )}
      aria-labelledby="portfolio-activity-heading"
    >
      <header className="flex items-center justify-between border-b border-ink-100 px-5 py-4">
        <div>
          <h2 id="portfolio-activity-heading" className="text-h3 font-semibold text-ink-900">
            Activity
          </h2>
          <p className="text-meta text-ink-500">Latest changes across your engagements</p>
        </div>
        <Link
          to="/notifications"
          className="text-meta font-medium text-ink-700 no-underline hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          View all
        </Link>
      </header>

      {isError ? (
        <div className="p-5">
          <ErrorMessage error={error} />
        </div>
      ) : isLoading && items.length === 0 ? (
        <div className="flex items-center gap-2 p-5 text-body-sm text-ink-500">
          <Spinner /> Loading activity…
        </div>
      ) : items.length === 0 ? (
        <div className="p-5 text-body-sm text-ink-500">No recent activity in your engagements.</div>
      ) : (
        <ul className="divide-y divide-ink-100">
          {items.map((item) => (
            <li key={item.event.id}>
              <ActivityRow item={item} />
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

function ActivityRow({ item }: { item: ActivityFeedItem }) {
  const { event } = item;
  const tone = eventToneClass(event.event_type);
  const summary = describeAuditEvent(event);
  const when = formatRelativeTime(event.created_at);

  return (
    <Link
      to="/engagements/$engagementId"
      params={{ engagementId: item.engagement_id }}
      className="block px-5 py-3 no-underline transition-colors hover:bg-paper-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
      aria-label={`Open ${item.engagement_name} — ${summary}`}
      title={auditOperationLabel(event)}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className={cn('mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full', tone)}
        />
        <div className="min-w-0 flex-1">
          <div className="text-body-sm leading-snug text-ink-700">
            <span className="font-semibold text-ink-900">{item.engagement_name}</span>{' '}
            <span className="text-ink-500">· {summary}</span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-meta text-ink-500">
            <span>{when}</span>
            <span aria-hidden="true">·</span>
            <span className="truncate">{item.client_name}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function eventToneClass(eventType: string): string {
  switch (eventType) {
    case 'override':
    case 'access_denied':
      return 'bg-posture-critical';
    case 'proposal':
    case 'proposal_decide':
      return 'bg-ai-surface-border';
    case 'delete':
      return 'bg-posture-at-risk';
    case 'create':
    case 'update':
    default:
      return 'bg-posture-unassessed';
  }
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const now = Date.now();
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}
